import { Result } from '@/classes/result';
import { Feature } from '@/enums/feature';
import { OrganizationRole } from '@/enums/roles';
import { ErrorMessage } from '@/messages/error';
import router, { redirectToStartPage } from '@/router';
import { useEmployeeStore } from '@/stores/employee';
import { useOrganizationStore } from '@/stores/organization';
import type { Employee } from '@/types/employee';
import type { JwtTokenContent } from '@/types/jwtToken';
import type { AccessibleOrganization, Organization } from '@/types/organization';
import { hasAccessToOrg } from '@/utils/auth';
import { handleAxiosError } from '@/utils/error';
import { getJwtTokenContent } from '@/utils/jwtToken';
import { getEmployeeFromOrganization } from '@/utils/organization';
import toast from '@/utils/toast';
import { computedAsync } from '@vueuse/core';
import { jwtDecode } from 'jwt-decode';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useAuthStore = defineStore('auth', () => {
    const loggedInEmployee = ref<Employee>();
    const currentOrganization = ref<AccessibleOrganization>();
    const accessibleOrganizations = ref<AccessibleOrganization[]>();
    const isAuthenticated = computed(() => !!loggedInEmployee.value);
    const isActionCoordinator = computed(() => organizationEmployeeHasFeature(Feature.ActionCoordinatorRole));
    const isAdmin = computed(() => !isActionCoordinator.value && isAuthorized([OrganizationRole.Admin]));
    const isSecurityOfficer = computed(() => loggedInEmployee.value?.isSecurityOfficer || loggedInEmployee.value?.isSuperAdmin);
    const isAdminOrActionCoordinator = computed(() => isAdmin.value || isActionCoordinator.value);
    const employeesMayViewDocuments = computed(async () => await rootOrganizationHasFeature(Feature.EmployeesMayViewDocuments));
    const isActionPointExecutorFeatureEnabled = computedAsync(async () => await rootOrganizationHasFeature(Feature.ActionPointExecutor));

    const areMultipleOrganizationsAccessible = computed(() => {
        if (!accessibleOrganizations.value) return false;
        if (accessibleOrganizations.value.length > 1) return true;

        const firstChildOrg = accessibleOrganizations.value[0];
        if (firstChildOrg?.children && firstChildOrg.children.length > 0) return true;

        return false;
    });

    function getLoggedInEmployee(): Employee {
        if (!loggedInEmployee.value) logout();
        return loggedInEmployee.value!;
    }

    function getCurrentOrganization() {
        if (!currentOrganization.value && loggedInEmployee.value) router.push({ name: 'organization-switch' });
        return currentOrganization.value!;
    }

    function clearSessionStates(): void {
        loggedInEmployee.value = undefined;
        currentOrganization.value = undefined;
    }

    function logout(): void {
        localStorage.removeItem('token');
        delete loggedInEmployee.value;
        clearSessionStates();
    }

    async function login(email: string, password?: string): Promise<Result> {
        try {
            const employeeStore = useEmployeeStore();
            const result: Result = await employeeStore.login(email, password);

            const jwtToken = result.object.toString();
            const decodedJwtToken: JwtTokenContent = jwtDecode(jwtToken);
            const employeeId = decodedJwtToken.employeeId;

            if (!employeeId) return new Result(500, 'Kon medewerker niet ophalen');

            localStorage.setItem('token', jwtToken);

            const employee = await employeeStore.fetchById(employeeId);
            if (employee == null) return new Result(404, ErrorMessage.EMPLOYEE_NOT_FOUND);

            loggedInEmployee.value = employee;

            toast.success('Welcome', { name: employee.firstName });
            await redirectToStartPage(employee);

            return result;
        } catch (error) {
            return handleAxiosError(error, []);
        }
    }

    /**
     * Pinia states are discarded when the page is refreshed.
     * This function checks if the user is already logged in and refreshes the state if necessary.
     */
    async function refreshLoginState(): Promise<void> {
        if (!localStorage.getItem('token')) {
            logout();
            return;
        }

        if (isAuthenticated.value) return;

        const jwtTokenContent = getJwtTokenContent();
        if (!jwtTokenContent.employeeId) return;
        const employeeStore = useEmployeeStore();

        const employee = await employeeStore.fetchById(jwtTokenContent.employeeId);
        if (!employee) return;

        loggedInEmployee.value = employee;

        if (!employee.currentOrganization) return;

        const organizationStore = useOrganizationStore();
        const currentOrg = await organizationStore.fetchById(employee.currentOrganization);
        if (currentOrg) currentOrganization.value = currentOrg;
    }

    function isSuperAdmin(): boolean {
        const employee = getLoggedInEmployee();
        if (!employee) return false;

        return employee.isSuperAdmin === true;
    }

    /**
     *
     * @param requiredRoles
     * @param organization if not specified the current organization will be used
     * @returns
     */
    function isAuthorized(requiredRoles: OrganizationRole[], organization?: Organization | null): boolean {
        if (requiredRoles.length == 0) return true;
        if (!isAuthenticated.value) return false;

        const authStore = useAuthStore();
        const employee = authStore.loggedInEmployee;

        if (!employee) return false;
        if (employee.isSuperAdmin) return true;

        if (!organization) {
            organization = authStore.getCurrentOrganization();
            if (!organization) return false;
        }

        const accessibleOrganizationIds = accessibleOrganizations.value?.map((organization: Organization) => organization._id);
        if (!accessibleOrganizationIds?.includes(organization._id)) return false;

        return hasAccessToOrg(employee._id, requiredRoles, organization, accessibleOrganizations.value ?? []);
    }

    /**
     * Check if the logged-in employee has a specific feature in the given organization or any parent org
     * @param feature
     * @param organization if not specified the current organization will be used
     * @returns
     */
    function organizationEmployeeHasFeature(feature: Feature, organization?: Organization | null): boolean {
        if (!isAuthenticated.value) return false;

        const authStore = useAuthStore();
        const employee = authStore.loggedInEmployee;

        if (!employee) return false;

        if (!organization) {
            organization = authStore.getCurrentOrganization();
            if (!organization) return false;
        }

        const accessibleOrganizationIds = accessibleOrganizations.value?.map((organization: Organization) => organization._id);
        if (!accessibleOrganizationIds?.includes(organization._id)) return false;

        let currentOrg: Organization | undefined = organization;

        while (currentOrg) {
            const orgEmployee = currentOrg.employees.find((oe) => {
                const empId = typeof oe.employee === 'string' ? oe.employee : oe.employee._id;
                return empId === employee._id;
            });

            if (orgEmployee?.features?.includes(feature)) return true;

            currentOrg = accessibleOrganizations.value?.find((o) => o._id === currentOrg?.parent);
        }

        return false;
    }

    /**
     * The readonly condition is matched when:
     * - The employee exists within the organization with isReadOnly = true
     * OR
     * - The employee is inside one of the parent organizations with role Admin and has isReadOnly = true
     *
     * Admin's read only state bubble downwards. Employee has independant read only states between organizations.
     */
    function isReadOnly(organization: Organization | null = null): boolean {
        const employee = getLoggedInEmployee();

        if (!employee) return false;
        if (employee.isSuperAdmin) return false;
        if (!organization) organization = getCurrentOrganization();

        const organizationEmployee = getEmployeeFromOrganization(organization, employee._id);

        if (organizationEmployee?.isReadOnly) return true;

        let parentId = organization!.parent;

        while (parentId) {
            const accessibleParentOrg = accessibleOrganizations.value?.find((org) => org._id === parentId);

            if (!accessibleParentOrg) break;

            const parentEmployee = getEmployeeFromOrganization(accessibleParentOrg, employee._id);

            if (parentEmployee?.isReadOnly && parentEmployee.roles?.includes(OrganizationRole.Admin)) return true;

            parentId = accessibleParentOrg.parent;
        }

        return false;
    }

    async function updateEmployeeOrganizations() {
        if (!loggedInEmployee.value) return;

        const employeeStore = useEmployeeStore();
        accessibleOrganizations.value = await employeeStore.getAccessibleOrganizations(loggedInEmployee.value._id);
    }

    function isLoggedInEmployee(employeeId: string): boolean {
        return loggedInEmployee.value?._id === employeeId;
    }

    /**
     * Checks if the root of the given organization contains the given feature.
     * @param organization defaults to authStore.currentOrganization
     */
    async function rootOrganizationHasFeature(feature: Feature, organization?: Organization | null) {
        const organizationStore = useOrganizationStore();
        if (!organization) organization = currentOrganization.value;
        if (!organization) return false;

        const features = await organizationStore.getFeatures(organization._id);
        return features.includes(feature);
    }

    return {
        login,
        logout,
        isAuthorized,
        organizationEmployeeHasFeature,
        isAuthenticated,
        isAdmin,
        isActionCoordinator,
        isAdminOrActionCoordinator,
        isSuperAdmin,
        isReadOnly,
        loggedInEmployee,
        isLoggedInEmployee,
        refreshLoginState,
        currentOrganization,
        accessibleOrganizations,
        areMultipleOrganizationsAccessible,
        updateEmployeeOrganizations,
        getLoggedInEmployee,
        getCurrentOrganization,
        isSecurityOfficer,
        rootOrganizationHasFeature,
        employeesMayViewDocuments,
        isActionPointExecutorFeatureEnabled
    };
});
